/**
 * SupplierInvoiceDetailModal — Modal de detalle de factura de proveedor.
 * Muestra encabezado, proveedor, líneas, metadatos RCV, estado contable.
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, Clock, ChevronDown, ChevronRight, AlertTriangle, FileCode, Link2, Edit2, Plus, Trash2, Loader2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { fmtDate, fmtDateTime } from '@/lib/utils'
import { formatMoneyForTenant, tenantCurrency, useTenantLocale } from '@/lib/locale'
import {
  useUpdateInvoiceReferencias,
  useUpdateInvoiceNotes,
  useSupplierInvoiceAttachments,
  useUploadSupplierInvoiceOriginalPdf,
  useDeleteSupplierInvoiceAttachment,
  useSupplierPayments,
  useCancelSupplierPayment,
  nonBillableAmount,
  payableTotal,
  type SupplierInvoice,
  type SupplierInvoiceStatus,
  type SupplierPaymentListItem,
  type APInvoiceReference,
} from '../hooks/usePurchases'
import { displayLabelCls as labelCls } from '@/lib/formStyles'
import apiClient, { getApiErrorMessage } from '@/lib/apiClient'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { usePermission } from '@/hooks/usePermission'
import { useRef } from 'react'
import { FileDown, Upload } from 'lucide-react'
import { XmlViewer, type XmlVariant } from '@/components/XmlViewer'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { Modal } from '@/components/ds/Modal'
import { CompletarFacturaPendiente } from './CompletarFacturaPendiente'

function DocumentsSection({ inv }: { inv: SupplierInvoice }) {
  const { can } = usePermission()
  const canEdit = can('purchases.invoices', 'edit')
  const { data } = useSupplierInvoiceAttachments(inv.id)
  const upload = useUploadSupplierInvoiceOriginalPdf(inv.id)
  const del = useDeleteSupplierInvoiceAttachment(inv.id)
  const fileRef = useRef<HTMLInputElement>(null)
  const original = (data?.attachments || []).find(a => a.kind === 'original_pdf')

  const openClobiPdf = async () => {
    try {
      const res = await apiClient.get(`/purchases/invoices/${inv.id}/pdf/`, { responseType: 'blob' })
      window.open(URL.createObjectURL(res.data), '_blank')
    } catch { /* ignore */ }
  }

  return (
    <div>
      <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-2">Documentos</p>
      <div className="flex flex-wrap items-center gap-2">
        {/* PDF original (subido / recibido) — abre en pestaña */}
        {original ? (
          <span className="inline-flex items-center gap-1">
            <a href={original.download_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content border border-divider rounded-lg hover:bg-soft transition-colors">
              <FileDown size={13} />PDF original
            </a>
            {canEdit && original.source !== 'voucher' && (
              <button type="button" onClick={() => del.mutate(original.id)} disabled={del.isPending}
                className="p-1 rounded text-content-subtle hover:text-danger-content-soft" title="Quitar PDF original">
                <Trash2 size={13} />
              </button>
            )}
          </span>
        ) : canEdit ? (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); if (fileRef.current) fileRef.current.value = '' }} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content border border-divider rounded-lg hover:bg-soft transition-colors disabled:opacity-60"
              title="Subir el PDF original del documento del proveedor">
              {upload.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}Subir PDF original
            </button>
          </>
        ) : (
          <span className="text-xs text-content-subtle">Sin PDF original</span>
        )}

        {/* PDF generado por Clobi — abre en pestaña */}
        <button type="button" onClick={openClobiPdf}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
          <FileDown size={13} />PDF generado por Clobi
        </button>
      </div>

      {/* XML del documento (solo si la factura tiene XML recibido/importado) */}
      {inv.has_xml && (
        <div className="mt-3">
          <XmlViewer
            title=""
            variants={[{
              key: 'xml',
              label: 'Ver XML',
              filename: `FC-${inv.invoice_number}.xml`,
              fetcher: async () => {
                const res = await apiClient.get(`/purchases/invoices/${inv.id}/xml/`, { responseType: 'blob' })
                return await (res.data as Blob).text()
              },
            } satisfies XmlVariant]}
          />
        </div>
      )}
    </div>
  )
}

interface Props {
  open: boolean
  onClose: () => void
  invoice: SupplierInvoice | null
  onApprove?: (id: string) => void
  onCancel?: (id: string) => void
  onDelete?: (id: string) => void
  approving?: boolean
  cancelling?: boolean
  deleting?: boolean
  /** Acuse/Reclamo SII (Ley 19.983) — reciben el id del InboundDTE vinculado.
   *  Solo se pasan si el usuario tiene permiso (dte_inbound.approve). */
  /** `null` = factura sin XML (#1637): se responde sobre la factura del RCV. */
  onAcceptSii?: (inboundDteId: string | null) => void
  onRejectSii?: (inboundDteId: string | null) => void
  acceptingSii?: boolean
  rejectingSii?: boolean
  /** Factura «Pendiente XML»: qué puede hacer el usuario para completarla (#1578). */
  puedeCargarXml?: boolean
  puedeAprobar?: boolean
  aprobarSinXml?: boolean
}

const fieldCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content min-h-[38px]'
const sectionTitleCls = 'text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3'

const STATUS_CONFIG: Record<SupplierInvoiceStatus, { label: string; cls: string }> = {
  rcv_pending:   { label: 'Pendiente XML (RCV)',cls: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
  rcv_reclamado: { label: 'Reclamado (RCV)',    cls: 'bg-danger-surface text-danger-content' },
  registered:    { label: 'Registrada',         cls: 'bg-surface-muted text-content-muted' },
  approved:      { label: 'Aprobada',           cls: 'bg-warning-surface text-warning-content' },
  paid:          { label: 'Pagada',             cls: 'bg-success-surface text-success-content' },
  cancelled:     { label: 'Anulada',            cls: 'bg-surface-muted text-content-muted' },
}

const DTE_TYPE_LABELS: Record<number, string> = {
  33: 'Factura Electrónica',
  34: 'Factura Exenta',
  39: 'Boleta Electrónica',
  46: 'Factura de Compra',
  56: 'Nota de Débito',
  61: 'Nota de Crédito',
}

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Ingreso Manual',
  xml_import: 'Importación XML',
  rcv: 'RCV SII',
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className={fieldCls}>{value ?? '—'}</div>
    </div>
  )
}

const formatCLP = (v: string | number) => {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return formatMoneyForTenant(n)
}

const fmtMoney = (amount: string, currency?: string) => {
  // Sin moneda declarada, el monto esta en la del tenant. Antes se asumia CLP,
  // asi que en un tenant peruano un pago en soles se imprimia con `$` y sin
  // centimos.
  const cur = (currency || tenantCurrency()).toUpperCase()
  return formatMoneyForTenant(amount, cur)
}

/**
 * Totales del documento — incluido el **monto no facturable**.
 *
 * ⚠️ Un documento puede tener **total tributario $0 y un monto real de millones**: el SII
 * lo informa como «no facturable» y no como neto ni exento. Pasa en cada compra de divisas
 * a una corredora, y también en telecom (equipos en cuotas, cargos de terceros). Mostrar
 * solo el total dejaba la pantalla diciendo «Total $0» junto a un pago aplicado por el
 * monto completo, sin ninguna pista de dónde salió esa plata.
 *
 * El listado de compras y la Vista 360 ya lo mostraban con los mismos helpers; este modal
 * era el único que no — y es el que se abre al hacer clic en la factura.
 */
function TotalsSection({ inv }: { inv: SupplierInvoice }) {
  const tax = useTenantTax()
  const totalNum = parseFloat(inv.total || '0')
  // Con total tributario 0, el no facturable ES el monto del documento. Con total > 0 el
  // extra lo resuelve `payableTotal` (VlrPagar = afecto + cargos de terceros).
  const noFacturable = totalNum === 0 ? nonBillableAmount(inv.sii_montos) : null
  const valorAPagar = payableTotal(inv.sii_montos, inv.total || '0')
  const aPagar = noFacturable || valorAPagar

  return (
    <div className="flex justify-end">
      <div className="w-72 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-content-muted">Subtotal (Neto)</span>
          <span className="text-content">{formatCLP(inv.subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-content-muted">{tax.name}</span>
          <span className="text-content">{formatCLP(inv.tax_amount)}</span>
        </div>
        <div className={`flex justify-between text-sm border-t border-divider pt-1.5 ${
          aPagar ? 'text-content-muted' : 'font-semibold text-content'}`}>
          <span>Total {aPagar ? 'tributario' : ''}</span>
          <span className={aPagar ? '' : 'text-content'}>{formatCLP(inv.total)}</span>
        </div>
        {aPagar && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-warning">No facturable</span>
              <span className="text-warning font-medium">{formatCLP(aPagar)}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold border-t border-divider pt-1.5">
              <span className="text-content">Total a pagar</span>
              <span className="text-content">
                {formatCLP(valorAPagar && !noFacturable ? valorAPagar : aPagar)}
              </span>
            </div>
            <p className="text-[11px] text-content-subtle leading-snug pt-1">
              El SII informa este documento con el monto como <strong>no facturable</strong>,
              fuera del neto y del exento{noFacturable ? ', por eso su total tributario es $0' : ''}.
              Es lo que corresponde pagar y lo que se contabiliza.
            </p>
          </>
        )}
        {inv.currency_original && inv.total_original && inv.currency_original !== 'CLP' && (
          <div className="flex justify-between text-xs text-content-subtle">
            <span>Total moneda original</span>
            <span>
              {inv.currency_original} {parseFloat(inv.total_original).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Pagos aplicados a la factura (fecha, monto, método, referencia, estado, quién).
 * Permite anular un pago mal imputado sin salir del detalle (revierte el
 * comprobante si estaba contabilizado). Pedido por Beatriz: poder ver y corregir
 * la imputación de pagos desde la factura (caso FD2594455).
 */
function PaymentsSection({ inv }: { inv: SupplierInvoice }) {
  const { data, isLoading } = useSupplierPayments({ invoice_id: inv.id })
  const cancelPayment = useCancelSupplierPayment()
  const { can } = usePermission()
  const { confirm, modalProps } = useConfirmModal()
  const [error, setError] = useState<string | null>(null)

  const payments = data?.payments ?? []
  const canCancel = can('purchases.payments', 'delete')

  const onCancelPayment = (p: SupplierPaymentListItem) => {
    setError(null)
    confirm({
      message: `¿Anular el pago de ${fmtMoney(p.amount, p.currency)} del ${fmtDate(p.payment_date)}? `
        + 'Si el pago está contabilizado, se revertirá su comprobante.',
      variant: 'danger',
      confirmLabel: 'Anular pago',
      onConfirm: async () => {
        try { await cancelPayment.mutateAsync(p.id) }
        catch (e) { setError(getApiErrorMessage(e)) }
      },
    })
  }

  return (
    <div>
      <p className={sectionTitleCls}>Pagos aplicados ({payments.filter(p => p.status !== 'cancelled').length})</p>
      {error && (
        <div className="mb-2 text-sm text-danger-content-soft bg-danger-surface border border-danger-border rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      {isLoading ? (
        <div className="text-center py-5 bg-glass-3 rounded-lg border border-divider text-sm text-content-subtle">
          <Loader2 size={15} className="animate-spin inline mr-1.5" /> Cargando pagos…
        </div>
      ) : payments.length === 0 ? (
        <div className="text-center py-5 bg-glass-3 rounded-lg border border-divider">
          <p className="text-sm text-content-subtle">Sin pagos registrados.</p>
        </div>
      ) : (
        <DataTable
          columns={[
            { key: 'payment_date', header: 'Fecha', render: p => <span className="whitespace-nowrap">{fmtDate(p.payment_date)}</span> },
            {
              key: 'amount', header: 'Monto', numeric: true,
              sortValue: p => Number(p.amount),
              render: p => (
                <span className="whitespace-nowrap">
                  <span className="font-medium text-content">{fmtMoney(p.amount, p.currency)}</span>
                  {p.currency && p.currency.toUpperCase() !== 'CLP' && p.amount_clp && (
                    <span className="block text-xs font-normal text-content-subtle">
                      ≈ ${parseFloat(p.amount_clp).toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </span>
              ),
            },
            { key: 'payment_method_display', header: 'Método' },
            { key: 'reference', header: 'Referencia', render: p => p.reference || '—' },
            { key: 'status_display', header: 'Estado' },
            { key: 'created_by_name', header: 'Registró', render: p => p.created_by_name || '—' },
            {
              key: 'acciones', header: '',
              render: p => (
                <div className="text-right">
                  {canCancel && ['registered', 'confirmed'].includes(p.status) && (
                    <button
                      type="button"
                      onClick={() => onCancelPayment(p)}
                      disabled={cancelPayment.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-danger-content-soft hover:bg-danger-surface rounded-lg disabled:opacity-50"
                    >
                      <XCircle size={13} /> Anular
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          rows={payments}
          rowKey={p => p.id}
          // Un pago anulado se atenúa la fila ENTERA.
          rowClassName={p => (p.status === 'cancelled' ? 'opacity-50' : undefined)}
          surface={false}
          minWidth={820}
          caption="Pagos de la factura"
        />
      )}
      <ConfirmModal {...modalProps} />
    </div>
  )
}

export function SupplierInvoiceDetailModal({ open, onClose, invoice, onApprove, onCancel, onDelete, approving, cancelling, deleting, onAcceptSii, onRejectSii, acceptingSii, rejectingSii, puedeCargarXml = false, puedeAprobar = false, aprobarSinXml = false }: Props) {
  const locale = useTenantLocale()
  const [rcvOpen, setRcvOpen] = useState(true)
  const [editingRefs, setEditingRefs] = useState(false)
  const [draftRefs, setDraftRefs] = useState<APInvoiceReference[]>([])
  const updateRefs = useUpdateInvoiceReferencias(invoice?.id ?? '')
  const [editingNotes, setEditingNotes] = useState(false)
  const [draftNotes, setDraftNotes] = useState('')
  const updateNotes = useUpdateInvoiceNotes(invoice?.id ?? '')

  useEffect(() => {
    if (!open) { setRcvOpen(true); setEditingRefs(false); setEditingNotes(false) }
    if (open && invoice) { setDraftRefs(invoice.referencias ?? []); setDraftNotes(invoice.notes ?? '') }
  }, [open, invoice])

  if (!open || !invoice) return null

  const inv = invoice
  const st = STATUS_CONFIG[inv.status] ?? STATUS_CONFIG.registered
  const dteLabel = inv.document_type_sii ? (DTE_TYPE_LABELS[inv.document_type_sii] ?? `DTE ${inv.document_type_sii}`) : null
  const isRcvPending = inv.status === 'rcv_pending' || inv.status === 'rcv_reclamado'

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={dteLabel ? `${dteLabel} #${inv.invoice_number}` : `Factura #${inv.invoice_number}`}
      subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>{inv.supplier_name}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
            {inv.accounting_status === 'accounted' && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-surface text-success-content">Contabilizado</span>
            )}
            {inv.accounting_status === 'pending' && inv.status === 'approved' && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">Sin contabilizar</span>
            )}
            {inv.sii_acuse?.accepted && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-surface text-success-content">Acuse SII enviado</span>
            )}
            {inv.sii_acuse?.rejected && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-danger-surface text-danger-content">Reclamado al SII</span>
            )}
          </span>
        }
      dismissOnScrimClick={false}
      actions={
        <>
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-content-subtle">
            Creada {inv.created_at ? fmtDateTime(inv.created_at, locale) : ''}
          </div>
          <div className="flex items-center gap-2">
            {inv.status === 'registered' && onApprove && (
              <button onClick={() => onApprove(inv.id)} disabled={approving}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface disabled:opacity-60">
                <CheckCircle2 size={14} />{approving ? 'Aprobando...' : 'Aprobar'}
              </button>
            )}
            {(inv.status === 'registered' || inv.status === 'approved') && onCancel && (
              <button onClick={() => onCancel(inv.id)} disabled={cancelling}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface disabled:opacity-60">
                <XCircle size={14} />{cancelling ? 'Anulando...' : 'Anular'}
              </button>
            )}
            {inv.status !== 'paid' && onDelete && (
              <button onClick={() => onDelete(inv.id)} disabled={deleting}
                title="Eliminar definitivamente la factura"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface disabled:opacity-60">
                <Trash2 size={14} />{deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            )}
            {/* Acuse / Reclamo SII (Ley 19.983) del DTE recibido vinculado (#432 PR3) */}
            {(inv.sii_acuse?.can_accept || (inv.sii_acuse?.sin_xml && inv.sii_acuse.can_acuse)) && onAcceptSii && (
              <button onClick={() => onAcceptSii(inv.sii_acuse!.inbound_dte_id)} disabled={acceptingSii}
                title="Enviar acuse de recibo al SII (Ley 19.983)"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60">
                <CheckCircle2 size={14} />{acceptingSii ? 'Enviando...' : 'Aceptar (acuse SII)'}
              </button>
            )}
            {inv.sii_acuse?.can_reject && onRejectSii && (
              <button onClick={() => onRejectSii(inv.sii_acuse!.inbound_dte_id)} disabled={rejectingSii}
                title="Reclamar (rechazar) el documento ante el SII — revierte el import si ya estaba en Compras"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface disabled:opacity-60">
                <XCircle size={14} />{rejectingSii ? 'Reclamando...' : 'Reclamar (SII)'}
              </button>
            )}
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-content-muted border border-divider rounded-lg hover:bg-soft">
              Cerrar
            </button>
          </div>

          </div>
        </>
      }
    >
      <div className="space-y-6">
        {/* Pendiente XML: cómo completarla (#1578). Reclamada en el SII: no se completa. */}
        {inv.status === 'rcv_reclamado' && (
          <div className="p-3 rounded-lg border bg-danger-surface border-danger-border flex items-center gap-2">
            <AlertTriangle size={16} className="text-danger-solid" />
            <p className="text-sm font-medium text-danger-content">Este documento fue reclamado en el SII. Consultar con el contador.</p>
          </div>
        )}
        {inv.status === 'rcv_pending' && (
          <CompletarFacturaPendiente factura={inv} puedeCargar={puedeCargarXml} puedeAprobar={puedeAprobar}
            aprobarSinXml={aprobarSinXml} aprobando={approving} onAprobar={onApprove ? () => onApprove(inv.id) : undefined} />
        )}

        {/* Datos generales */}
        <div>
          <p className={sectionTitleCls}>Datos del Documento</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Nro. Factura" value={inv.invoice_number} />
            <Field label="Tipo DTE" value={dteLabel ?? 'N/A'} />
            <Field label="Fecha Emisión" value={inv.issue_date ? fmtDate(inv.issue_date, locale) : undefined} />
            <Field label="Fecha Vencimiento" value={inv.due_date ? fmtDate(inv.due_date, locale) : undefined} />
          </div>
        </div>

        {/* Proveedor */}
        <div>
          <p className={sectionTitleCls}>Proveedor</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Razón Social" value={inv.supplier_name} />
            <Field label="Origen" value={SOURCE_LABELS[inv.source] ?? inv.source} />
          </div>
        </div>

        {/* Metadatos RCV */}
        {(inv.rcv_fecha_recepcion || inv.rcv_estado) && (
          <div>
            <button type="button" onClick={() => setRcvOpen(v => !v)}
              className="flex items-center gap-2 w-full text-left">
              {rcvOpen ? <ChevronDown size={14} className="text-content-subtle" /> : <ChevronRight size={14} className="text-content-subtle" />}
              <p className={sectionTitleCls + ' mb-0'}>Datos RCV (SII)</p>
            </button>
            {rcvOpen && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                <Field label="Estado RCV" value={inv.rcv_estado} />
                <Field label="Recepción SII" value={inv.rcv_fecha_recepcion ? fmtDate(inv.rcv_fecha_recepcion, locale) : undefined} />
                <Field label="Fecha Acuse" value={inv.rcv_fecha_acuse ? fmtDate(inv.rcv_fecha_acuse, locale) : undefined} />
                <Field label="Fecha Reclamo" value={inv.rcv_fecha_reclamo ? fmtDate(inv.rcv_fecha_reclamo, locale) : undefined} />
                <Field label="Deadline Acuse/Reclamo" value={inv.rcv_deadline ? fmtDate(inv.rcv_deadline, locale) : undefined} />
                {inv.days_until_deadline != null && (
                  <div>
                    <p className={labelCls}>Plazo Restante</p>
                    <div className={fieldCls}>
                      {inv.days_until_deadline < 0 ? (
                        <span className="inline-flex items-center gap-1 text-danger-content-soft font-medium">
                          <Clock size={13} />Vencido ({Math.abs(inv.days_until_deadline)} días)
                        </span>
                      ) : inv.days_until_deadline <= 2 ? (
                        <span className="inline-flex items-center gap-1 text-orange-600 dark:text-orange-300 font-medium">
                          <Clock size={13} />{inv.days_until_deadline} días
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-primary">
                          <Clock size={13} />{inv.days_until_deadline} días
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Referencias (NC/ND) */}
        {(inv.document_type_sii === 56 || inv.document_type_sii === 61 ||
          inv.document_type_sii === 111 || inv.document_type_sii === 112 ||
          (inv.referencias && inv.referencias.length > 0)) && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className={sectionTitleCls}>Referencias ({(inv.referencias ?? []).length})</p>
              {inv.status !== 'cancelled' && !editingRefs && (
                <button
                  onClick={() => { setDraftRefs(inv.referencias ?? []); setEditingRefs(true) }}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover"
                >
                  <Edit2 size={12} />Editar
                </button>
              )}
            </div>
            {inv.is_adjustment_orphan && (
              <div className="mb-2 px-3 py-2 text-xs text-danger-content bg-danger-surface border border-danger-border rounded-md flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>Las referencias de esta NC/ND no coinciden con ninguna factura en el sistema. Edita para corregir la asignación.</span>
              </div>
            )}
            {!editingRefs ? (
              (inv.referencias ?? []).length > 0 ? (
                <DataTable
                  columns={[
                    { key: 'tipo_doc', header: 'Tipo' },
                    { key: 'folio_ref', header: 'Folio', render: r => <span className="font-mono text-content">{r.folio_ref}</span> },
                    { key: 'fecha_ref', header: 'Fecha', render: r => r.fecha_ref || '—' },
                    { key: 'cod_ref', header: 'Cod.', render: r => r.cod_ref || '—' },
                    { key: 'razon_ref', header: 'Razón', render: r => r.razon_ref || '—' },
                  ]}
                  rows={inv.referencias ?? []}
                  rowKey={(_, i) => i}
                  surface={false}
                  minWidth={520}
                  caption="Referencias del documento"
                />
              ) : (
                <p className="text-xs text-content-subtle">Sin referencias registradas.</p>
              )
            ) : (
              <div className="space-y-2">
                {draftRefs.map((ref, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <select aria-label={`Tipo de documento — referencia ${i + 1}`} value={ref.tipo_doc} onChange={e => setDraftRefs(r => r.map((x, idx) => idx === i ? { ...x, tipo_doc: e.target.value } : x))}
                      className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg">
                      <option value="33">33 FE</option>
                      <option value="34">34 FNA</option>
                      <option value="46">46 FC</option>
                      <option value="110">110 FEX</option>
                    </select>
                    <input aria-label={`Folio — referencia ${i + 1}`} value={ref.folio_ref} onChange={e => setDraftRefs(r => r.map((x, idx) => idx === i ? { ...x, folio_ref: e.target.value } : x))}
                      className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg" placeholder="Folio" />
                    <input aria-label={`Fecha — referencia ${i + 1}`} type="date" value={ref.fecha_ref} onChange={e => setDraftRefs(r => r.map((x, idx) => idx === i ? { ...x, fecha_ref: e.target.value } : x))}
                      className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg" />
                    <select aria-label={`Código de referencia — referencia ${i + 1}`} value={ref.cod_ref} onChange={e => setDraftRefs(r => r.map((x, idx) => idx === i ? { ...x, cod_ref: e.target.value } : x))}
                      className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg">
                      <option value="">—</option>
                      <option value="1">1 Anula</option>
                      <option value="2">2 Texto</option>
                      <option value="3">3 Monto</option>
                    </select>
                    <input aria-label={`Razón — referencia ${i + 1}`} value={ref.razon_ref} onChange={e => setDraftRefs(r => r.map((x, idx) => idx === i ? { ...x, razon_ref: e.target.value } : x))}
                      className="col-span-3 px-2 py-1.5 text-sm border border-divider rounded-lg" placeholder="Razón" />
                    <button onClick={() => setDraftRefs(r => r.filter((_, idx) => idx !== i))}
                      className="col-span-1 p-1.5 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded-lg justify-self-center">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2">
                  <button onClick={() => setDraftRefs(r => [...r, { tipo_doc: '33', folio_ref: '', fecha_ref: '', cod_ref: '1', razon_ref: '' }])}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover">
                    <Plus size={12} />Agregar
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingRefs(false)}
                      className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-soft">
                      Cancelar
                    </button>
                    <button
                      onClick={() => updateRefs.mutate(draftRefs.filter(r => r.folio_ref), {
                        onSuccess: () => setEditingRefs(false),
                      })}
                      disabled={updateRefs.isPending}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60"
                    >
                      {updateRefs.isPending && <Loader2 size={12} className="animate-spin" />}
                      Guardar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Líneas de detalle */}
        <div>
          <p className={sectionTitleCls}>Detalle ({inv.lines?.length ?? 0} líneas)</p>
          {inv.lines && inv.lines.length > 0 ? (
            <DataTable
              columns={[
                {
                  key: 'name', header: 'Descripción',
                  render: line => (
                    <>
                      {line.name || line.sku || 'Item'}
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {(line as any).description && (
                        <p className="text-xs text-content-subtle mt-0.5 line-clamp-2">{(line as any).description}</p>
                      )}
                    </>
                  ),
                },
                { key: 'quantity', header: 'Cant.', numeric: true },
                { key: 'unit_price', header: 'P. Unit.', numeric: true, render: line => formatCLP(line.unit_price) },
                {
                  key: 'subtotal', header: 'Subtotal', numeric: true,
                  render: line => <span className="font-medium text-content">{formatCLP(line.subtotal || '0')}</span>,
                },
              ]}
              rows={inv.lines}
              rowKey={(_, i) => i}
              surface={false}
              minWidth={520}
              caption="Líneas de la factura de compra"
            />
          ) : (
            <div className="text-center py-6 bg-glass-3 rounded-lg border border-divider">
              <p className="text-sm text-content-subtle">
                {isRcvPending ? 'Las líneas se cargarán cuando llegue el XML.' : 'Sin líneas de detalle.'}
              </p>
              {/* El Registro de Compras del SII trae montos y no detalle: sin esta nota,
                  un documento que llegó solo por ahí se lee como si no tuviera monto. */}
              {!isRcvPending && parseFloat(inv.total || '0') === 0
                && nonBillableAmount(inv.sii_montos) && (
                <p className="text-xs text-content-subtle mt-1">
                  El documento llegó por el Registro de Compras del SII, que informa los
                  totales sin el detalle. El monto está más abajo, como no facturable.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Totales */}
        <TotalsSection inv={inv} />

        {/* Pagos aplicados — visibilidad + anular pago mal imputado */}
        <PaymentsSection inv={inv} />

        {/* Campos personalizados (compra) — solo lectura */}
        {Array.isArray((inv as any).personalizados) && (inv as any).personalizados.length > 0 && (
          <div>
            <p className={sectionTitleCls}>Campos personalizados</p>
            <dl className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {(inv as any).personalizados.map((p: any, i: number) => (
                <div key={i} className="flex justify-between gap-2 border-b border-divider/50 py-0.5">
                  <dt className="text-content-muted">{p.nombre}</dt>
                  <dd className="text-content font-medium text-right">
                    {p.tipo === 'boolean'
                      ? (p.valor === 'true' ? 'Sí' : 'No')
                      : (p.valor_label || p.valor || '—')}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Notas / Observaciones — editable en cualquier estado (no contable) */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className={sectionTitleCls}>Notas / Observaciones</p>
            {inv.status !== 'cancelled' && !editingNotes && (
              <button
                onClick={() => { setDraftNotes(inv.notes ?? ''); setEditingNotes(true) }}
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover"
              >
                <Edit2 size={12} />Editar
              </button>
            )}
          </div>
          {!editingNotes ? (
            inv.notes ? (
              <div className="px-3 py-2 text-sm bg-glass-3 rounded-lg border border-divider text-content-muted whitespace-pre-wrap">
                {inv.notes}
              </div>
            ) : (
              <p className="text-xs text-content-subtle">Sin notas.</p>
            )
          ) : (
            <div className="space-y-2">
              <textarea aria-label="Notas"
                value={draftNotes}
                onChange={e => setDraftNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Notas u observaciones internas…"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditingNotes(false)}
                  className="px-3 py-1.5 text-xs border border-divider rounded-lg text-content-muted hover:bg-soft"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => updateNotes.mutate(draftNotes, { onSuccess: () => setEditingNotes(false) })}
                  disabled={updateNotes.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
                >
                  {updateNotes.isPending && <Loader2 size={12} className="animate-spin" />}Guardar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Documentos: PDF original (subido/recibido) + PDF generado por Clobi */}
        <DocumentsSection inv={inv} />
      </div>
    </Modal>
  )
}
